import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorPage } from '@screentone/addon-auth-wrapper';
import { v4 as uuidv4 } from 'uuid';

import { ResponsiveLoader } from 'components';
import { AddEntityToCollection } from 'components/context-menu-actions/add-entity-to-collection/AddEntityToCollection';
import { RemoveEntity } from 'components/context-menu-actions/remove-entity/RemoveEntity';
import { ViewPublishedURL } from 'components/context-menu-actions/view-published-url/ViewPublishedURL';
import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import { AllessehContentUpdateProvider } from 'contexts/allesseh-content-update/AllessehContentUpdateContext';
import {
  ActionDefinition,
  ComponentFnParams,
  ContextMenuActionsProvider,
  MenuActions,
  MenuActionsKeys
} from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { DataModelProvider, DataModelType } from 'contexts/datamodel/DataModelContext';
import { DragAndDropProvider } from 'contexts/drag-and-drop/DragAndDropContext';
import {
  getNewsletterLayoutConfigById,
  PageTypeSettingsProvider
} from 'contexts/page-type-settings/PageTypeSettingsContext';
import { SummarianProvider } from 'contexts/summarian/SummarianContext';
import { TrashProvider } from 'contexts/trash/TrashContext';
import {
  ArticleItem,
  BaseMetadata,
  ContentUnion,
  ModuleContainer,
  NewsletterPageContainer,
  PageType,
  PageTypeSetting,
  usePageTypeSettingByPublicationKeyPageTypeQuery
} from 'data/generated/graphql';
import { getDefaultNewsletterModuleContainer } from 'features/newsletters/constants';
import { useEnabledNewsletter } from 'features/newsletters/hooks/useEnabledNewsletter';
import { useNewsletterTypes } from 'features/newsletters/hooks/useNewsletterTypes';
import { NewsletterEdit } from 'features/newsletters/NewsletterEdit';
import { useConvertedProperty } from 'hooks';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useCommandFSearch } from 'hooks/useCommandFSearch';
import { convertPageModuleItemsToDataModelEntities } from 'utils/allesseh';
import env from 'utils/env';
import { getTreatmentType } from 'utils/modules';
import { safelyParseContent } from 'utils/temp';

const NewsletterEditPage = () => {
  const currentProperty = useConvertedProperty();

  const [searchParams] = useSearchParams();
  const newsletterId = searchParams.get('newsletter');

  const { newsletterType, isFetching } = useNewsletterTypes({
    publicationKey: currentProperty ?? '',
    newsletterId: newsletterId ?? ''
  });

  const { isLoading, enabledNewsletter } = useEnabledNewsletter();

  const { data: pageTypeSettingsResp } = usePageTypeSettingByPublicationKeyPageTypeQuery(
    { publicationKey: currentProperty ?? '', pageType: PageType.Newsletter },
    { enabled: !!currentProperty }
  );

  const pageTypeSetting = pageTypeSettingsResp?.pageTypeSettingByPublicationKeyPageType as PageTypeSetting | undefined;

  const allessehId = useMemo(
    () => `${env.UCS_COLLECTION_PREFIX}_${currentProperty?.toUpperCase()}_NEWSLETTER_${newsletterId}`,
    [newsletterId, currentProperty]
  );

  const newsletterLayoutConfig = useMemo(
    () => getNewsletterLayoutConfigById(newsletterId ?? '', pageTypeSetting) ?? undefined,
    [newsletterId, pageTypeSetting]
  );

  const isBasicLayout = useMemo(
    () => newsletterLayoutConfig?.id === 'baseline' || newsletterLayoutConfig?.id === 'default',
    [newsletterLayoutConfig]
  );

  const newsletterModuleContainer = useMemo<ModuleContainer[]>(() => {
    if (!pageTypeSetting || !newsletterLayoutConfig) {
      return [];
    }

    return (newsletterLayoutConfig.requiredLayoutModules ?? []).map((module) => {
      const treatmentType =
        module.uiModuleFields?.basicModule?.treatmentType ?? module.uiModuleFields?.titleModule?.treatmentType;
      // SETTINGS_ONLY(structure-migration): treatmentTypeSettings comes from hardcoded SETTINGS; not populated for structure pubs
      const treatmentTypeSetting = pageTypeSetting.treatmentTypeSettings.find(
        (t) => t.treatmentType === treatmentType || t.treatmentTypeKey === treatmentType
      );
      // END SETTINGS_ONLY(structure-migration)

      return {
        ...getDefaultNewsletterModuleContainer(),
        collection: convertPageModuleItemsToDataModelEntities(module),
        attributes: {
          pageModule: module,
          moduleId: module.moduleId,
          brand: treatmentTypeSetting?.brand ?? pageTypeSetting.treatmentTypeSettings[0].brand
        }
      } as ModuleContainer;
    });
  }, [newsletterLayoutConfig, pageTypeSetting]);

  const contextActions: MenuActions = useMemo<MenuActions>(() => {
    const articleSearchActions: ActionDefinition[] = [];

    // Show "View Published URL" only if content is not in embargo
    const showIfNotEmbargo = ({ entity }: ComponentFnParams) => {
      const article = entity as ArticleItem;
      const content = safelyParseContent<AllessehContent>(article);
      return content.data.attributes.content_status !== 'embargo';
    };

    if (isBasicLayout) {
      articleSearchActions.push({
        component: ({ hierarchyId, entity }) => {
          const module = { ...newsletterModuleContainer[0] };
          module.collection = [entity as ContentUnion];

          return (
            <AddEntityToCollection
              entity={module}
              hierarchyId={hierarchyId!}
              textDisplay="Add to Campaign"
              dataTestId="newsletter-add-to-campaign-action"
            />
          );
        }
      });
    }

    articleSearchActions.push({
      component: ({ entity }) => {
        const article = entity as ArticleItem;
        return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
      },
      showIf: showIfNotEmbargo,
      withDivider: 'top'
    });

    return {
      [MenuActionsKeys.DraftArticle]: [
        {
          component: ({ hierarchyId }) => <RemoveEntity hierarchyId={hierarchyId!} text="Remove from module" />
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: showIfNotEmbargo,
          withDivider: 'top'
        }
      ],
      [MenuActionsKeys.ArticleSearch]: articleSearchActions,
      [MenuActionsKeys.DraftModule]: [
        {
          component: ({ hierarchyId = '' }) => (
            <RemoveEntity hierarchyId={hierarchyId} text="Remove module" confirmIfNotEmpty />
          ),
          showIf: ({ entity, extraProperties = {} }) => {
            const { addedModulesByTreatmentType = {}, moduleInfoByTreatmentType = {} } = extraProperties;
            const container = entity as ModuleContainer;

            if (container.attributes.pageModule) {
              const treatmentType = getTreatmentType(container.attributes.pageModule);

              if (
                treatmentType != null &&
                treatmentType in moduleInfoByTreatmentType &&
                treatmentType in addedModulesByTreatmentType
              ) {
                return (
                  addedModulesByTreatmentType[treatmentType] >
                  (moduleInfoByTreatmentType[treatmentType].minimumAllowed ?? 0)
                );
              }
            }

            return false;
          }
        }
      ]
    };
  }, [newsletterModuleContainer, isBasicLayout]);

  const root = useMemo<NewsletterPageContainer>(
    () => ({
      subtype: 'NewsletterPage',
      type: 'Page',
      uuid: uuidv4(),
      collection: [...newsletterModuleContainer],
      attributes: {
        campaignId: `${Math.random()}`.substring(2, 7),
        newsletterId: newsletterType?.id ? +newsletterType.id : 0,
        preHeaderText: newsletterType?.defaultCampaignDetails?.pre_header_text ?? '',
        replyToEmail: newsletterType?.defaultCampaignDetails?.reply_to_email ?? '',
        senderName: newsletterType?.defaultCampaignDetails?.sender_name ?? '',
        subject: newsletterType?.subject ?? '',
        layoutId: newsletterLayoutConfig?.id ?? ''
      }
    }),
    [newsletterModuleContainer, newsletterType, newsletterLayoutConfig?.id]
  );

  const metadata = useMemo<BaseMetadata>(
    () => ({
      allessehId,
      name: newsletterType?.name ?? '',
      publicationKey: currentProperty ?? '',
      createdUtc: 0,
      creatorUser: { djUserId: '' },
      revisorUser: { djUserId: '' }
    }),
    [newsletterType, currentProperty, allessehId]
  );

  // Enables article cards to be searched for using their Allesseh IDs via "command + f"
  useCommandFSearch();

  if (isFetching || isLoading) {
    return (
      <PageRootContainer>
        <ResponsiveLoader />
      </PageRootContainer>
    );
  }

  if (!currentProperty || !newsletterId || !newsletterType || !enabledNewsletter) {
    return <ErrorPage type="404" />;
  }

  return (
    <AllessehContentUpdateProvider>
      <SummarianProvider>
        <DataModelProvider type={DataModelType.NewsletterDTO} root={root} metadata={metadata}>
          <TrashProvider>
            <DragAndDropProvider>
              <PageRootContainer noBottomMargin>
                <ContextMenuActionsProvider actions={contextActions}>
                  <PageTypeSettingsProvider pageType={PageType.Newsletter}>
                    <NewsletterEdit />
                  </PageTypeSettingsProvider>
                </ContextMenuActionsProvider>
              </PageRootContainer>
            </DragAndDropProvider>
          </TrashProvider>
        </DataModelProvider>
      </SummarianProvider>
    </AllessehContentUpdateProvider>
  );
};

export default NewsletterEditPage;
